"use client";

import { cn } from '@/lib/utils';
import React, { useState, useEffect } from 'react';

// 定义 TypingEffectProps 接口，指定组件所需的属性
interface TypingEffectProps {
  strings: string[]; // 字符串数组，包含需要打字显示的内容
  typingSpeed?: number; // 打字速度
  deleteSpeed?: number; // 删除速度
  pauseDuration?: number; // 停顿时长
  className?: string;
}
type TypingStatus = 'outputing' | 'deleting' | 'pausing';

// 定义 TypingEffect 组件
export const TypingEffect = ({
  strings,
  typingSpeed = 150,
  deleteSpeed = 120,
  pauseDuration = 2500,
  className = '',
}: TypingEffectProps) => {
  // 使用 useState 定义组件状态
  const [currentStringIndex, setCurrentStringIndex] = useState(0); // 当前字符串的索引
  const [currentTypedText, setCurrentTypedText] = useState(''); // 当前已打印的文本内容
  const [isDeleting, setIsDeleting] = useState(false); // 是否正在删除文本内容
  const [typingDelay, setTypingDelay] = useState(typingSpeed); // 当前的打字间隔时间
  const [typingStatus, setTypingStatus] = useState<TypingStatus>('outputing'); // 是否正在删除文本内容

  // 使用 useEffect 实现打字、删除、停顿效果
  useEffect(() => {
    // 计算当前字符串的索引和内容
    const currentIndex = currentStringIndex % strings.length;
    const currentString = strings[currentIndex];

    // 设置定时器模拟打字、删除和停顿
    const typingTimer = setTimeout(() => {
      if (!isDeleting && currentTypedText.length < currentString.length) {
        // 如果不是删除状态且当前已打印的文本长度小于当前字符串长度，则继续打字
        setCurrentTypedText(currentString.substring(0, currentTypedText.length + 1));
        setTypingDelay(typingSpeed); // 设置打字速度
        setTypingStatus('outputing');
      } else if (isDeleting && currentTypedText.length > 0) {
        // 如果是删除状态且当前已打印的文本长度大于0，则继续删除
        setCurrentTypedText(currentTypedText.substring(0, currentTypedText.length - 1));
        setTypingDelay(deleteSpeed); // 设置删除速度
        setTypingStatus('deleting');
      } else {
        // 否则切换删除状态，并根据是否删除设置不同的延迟时间
        setIsDeleting(!isDeleting);
        if (!isDeleting) {
          setTypingDelay(pauseDuration); // 设置停顿时长
          setTypingStatus('pausing');
        }
      }
    }, typingDelay);

    // 在组件销毁时清除定时器
    return () => clearTimeout(typingTimer);
  }, [currentTypedText, currentStringIndex, isDeleting, typingSpeed, deleteSpeed, pauseDuration, strings, typingDelay]);

  // 使用 useEffect 实现自动切换到下一个字符串
  useEffect(() => {
    if (!isDeleting && currentTypedText === strings[currentStringIndex % strings.length]) {
      setCurrentStringIndex(currentStringIndex + 1); // 切换到下一个字符串
    }
  }, [currentTypedText, currentStringIndex, isDeleting, pauseDuration, strings]);

  // 返回打字效果的 JSX
  // 竖线 丨ᅵ∣ │ ┃▕ ❙〡ㅣ㆐丨ￜ￨｜
  return (
    <span className={cn(
      className
    )}>
      <span>{currentTypedText}</span>
      <span className={cn(
        "opacity-100 font-normal text-primary",
        typingStatus === 'pausing' ? "animate-typing-cursor-blink" : "",
      )}>∣</span>
    </span>
  );
};
